/*
 * © Copyright 2015
 * Landcare Research
 *
 * Dual License with
 *
 * GPL v3 - See http://www.gnu.org/licenses/gpl-3.0.txt
 *
 * Any derivative work needs to be contributed back to this project
 * unless otherwise agreed with Landcare Research, New Zealand.
 */
import {L} from "./landcare-toolkit";
import {SoilQuery} from './soil-query-service';
import type {LTMap} from "./lt-directives";
import {MapClickInteraction} from "./map-interactions";
import {HDPI_Setting, ISettingsService} from "../services/settings";
import {IToastService} from "../services/ToastService";
import {IAppConfig} from "../services/app-config/app_config";
import {FeaturesLayer} from "../services/features-api/features-layer";
import {ISavedFeatureService} from "../services/features-api/saved_features";
import {TileErrorMonitoringService} from "../services/tile-error-monitoring";
import {TileErrorModalComponent} from "../components/TileErrorModalComponent";
import {Map as olMap, View} from 'ol';
import {Layer, Vector} from 'ol/layer';
import {default as Base} from 'ol/layer/Base';
import {OverviewMap} from "ol/control";
import {Coordinate} from "ol/coordinate";
import {DragZoom, Interaction, KeyboardPan, Select} from "ol/interaction";
import {_F} from "../utility/func";
import KeyboardZoom from "ol/interaction/KeyboardZoom";
import {_t} from "../utility/i18n";
import VectorSource from "ol/source/Vector";
import {Geometry} from "ol/geom";
import {Source} from "ol/source";
import {IPortalServices} from "./portal-services";

type ZoomAnimation = "pan" | "bounce";

export module LTServices {
	import is_high_density = L.is_high_density;
	import Item = L.tree.Item;
	import updateMapSizeWhenVisible = _F.updateMapSizeWhenVisible;
	import mapNodes = L.tree.mapNodes;
	import filterNodes = L.tree.filterNodes;

	// import strokePattern = _F.strokePattern;
	export interface IMapService {
		treeData: any[];
		mapPromise: ng.IPromise<olMap>;
		layers: Layer<any>[];
		// pinLayer: Vector;
		// pinPolygonLayer: Vector;
		featuresLayer: Vector<VectorSource<Geometry>>;
		overviewLayer: OverviewMap;
		transparencySliderControl: L.TransparencySliderControl;
		promise: ng.IPromise<void>;
		view: View;
		map: olMap;
		configuration: any;
		zoomTo(coord : Coordinate, zoom : number, animation : ZoomAnimation) : void;
		setActiveClickInteraction(interaction: MapClickInteraction|Select) : ng.IPromise<void>;
		getActiveClickInteraction(): ng.IPromise<MapClickInteraction>;
		setMap(map: olMap): void;
		setDefaultClickInteraction(clickInteraction: MapClickInteraction): void;
		getDefaultClickInteraction(): MapClickInteraction;
		setDragZoom(on: boolean);
		getCustomLayerGroup() : Item;

		detachMap(): void;
		attachMap(): void;
		tourActive: boolean;
		reportOpen: boolean;
		keyboardPan: KeyboardPan;
		keyboardZoom: KeyboardZoom;
		addLayer(node: Item, silent?: boolean): ng.IPromise<void>
		removeLayer(node: Item, silent?: boolean): ng.IPromise<void>;
		nextId(): ng.IPromise<number>;
	}
	export interface IMapServiceFactory {
	}

	export class LandcareMapService implements IMapService, IMapServiceFactory {
		public treeData: any[];
		public layers: Layer<Source>[];
		// public pinLayer: Vector;
		// public pinPolygonLayer: Vector;
		public configuration: any;
		public featuresLayer: Vector<VectorSource<Geometry>>;
		public overviewLayer: OverviewMap;
		public transparencySliderControl: L.TransparencySliderControl;
		protected _promise: ng.IPromise<void>;

		public get promise(): ng.IPromise<void> {
			if (this._promise) return this._promise;

			const config = window['LTConfiguration'];

			const dataPromise = config && config['layers']
				? this.$q.resolve(config['layers'])
				: this.$http.get<any>(`${this.portalServices.baseUri()}/portal_configuration/?application=${this.appName}`)
					.then(resp => resp.data && resp.data.response);

            this._promise = this._promise || dataPromise
                .then((response: any) => {
                    if (!response) {
                        throw new Error('Layer configuration does not have the expected response property');
                    }

                    if (!window['LTConfiguration']) {
                        return; // This happens on factsheets page when logging events
                    }
                    window['LTConfiguration']['layers'] = response;


                    let obj = L.util.treeDataFromLayerJson(
                        this.$http,
                        response,
                        this.settingsService,
                        {
                            includeAllItems: this.appConfig.includeItemsByDefault(),
                            expandedGroups: this.appConfig.expandGroupsByDefault()
                        });
                    let layers: Layer<Source>[] = obj.layers;
                    // layers.push(this.pinPolygonLayer);
                    // layers.push(this.drawingLayer); // drawing layer underneath pin layer
                    // layers.push(pinLayer);
                    layers.push(this.featuresLayer);

                    const lx = this.appConfig.layerExtent;
                    if (lx && lx.length === 4) {
						layers.forEach(l => l.setExtent(lx));
					}

                    this.layers = layers;
                    this.treeData = obj.treeData;

                    this.transparencySliderControl = new L.TransparencySliderControl({
                        layers: this.map && this.map.getLayers(),
                        layerFilter: (layer: Base) => layer.get('transparencyLayer')
                    });

                    this.overviewLayer = obj.overviewLayer;
                    this.configuration = response.configuration;
                    this.$rootScope.$emit('treeData:loaded');
                }).catch((error) => {
                    // Do we need some error handling here...
                    console.warn(error);
                    this.toastService.showToast('Error: layer configuration service failed.')
                });
            return this._promise;
		}

		public mapPromise: ng.IPromise<olMap>;
		public view: View;
		public map: olMap;
		protected mapDeferred: ng.IDeferred<olMap>;
		private defaultClickInteraction: MapClickInteraction;
		private customLayerGroup: Item = null;
		protected appName: string;

        public static $inject = [
            '$http',
            // 'pinService',
            'savedFeatureService',
            'myQueryString.QueryString',
            '$rootScope',
            'settingsService',
            '$q',
            'toastService',
            'appConfig',
            'portalServices'
        ];
		constructor(protected $http: ng.IHttpService,
					protected savedFeatureService : ISavedFeatureService,
					protected qString : LTMap.IQueryString,
					protected $rootScope : ng.IRootScopeService,
					protected settingsService : ISettingsService,
					protected $q: ng.IQService,
					protected toastService: IToastService,
					protected appConfig: IAppConfig,
                    protected portalServices: IPortalServices) {
			this.appName = this.appConfig.getApplication();

			this.$rootScope.$on('settings:hdpi', (ev, newValue : HDPI_Setting) => {
				let hdpi_on = is_high_density();
				if (newValue == 'hdpi') {
					hdpi_on = true;
				}
				if (newValue == 'standard') {
					hdpi_on = false;
				}


				this.mapPromise.then((map) => {
					let theLayers = map.getLayers().getArray();
					theLayers.forEach((layer) => {
						layer.set('hdpi', hdpi_on);
					});
				});
			});

			this.featuresLayer = new FeaturesLayer({
				source: null
			});

			this.featuresLayer.setZIndex(10009);

			this.featuresLayer.setSource(savedFeatureService.featuresSource);
			this.defaultClickInteraction = null;
			this.mapDeferred = $q.defer<olMap>();
			this.mapPromise = this.mapDeferred.promise;

			this.mapPromise.then((map: olMap) => {
				window.scrollTo(0,document.body.scrollHeight);
				$rootScope.$broadcast('map:loaded', map);

				if(this.transparencySliderControl) this.transparencySliderControl.setLayers(map.getLayers());
			});
		}

		/**
		 * Get the layer tree node that holds a user's custom layers.
		 * @returns {L.tree.Item}
		 */
		getCustomLayerGroup(): L.tree.Item {
			if (!this.customLayerGroup) {
				this.customLayerGroup = {
					name: _t('LAYER_SELECTOR.WEB_DATA_LAYERS', 'Web data layers'),
					isChecked: L.tree.isChecked,
					children: [],
					layerSelectorChildren: [],
					checked: false,
					expanded: true,
					extraState: {
						metaLayerSelector: {
							checked: true,
							expanded: false
						}
					},
					click: L.tree.clickItem,
					index: 999,
					id: 999,
					rootItems: this.treeData
				};
				let context = this.treeData.shift();
				this.treeData.unshift(this.customLayerGroup);
				this.treeData.unshift(context);
			}
			return this.customLayerGroup
		}

		/**
		 * Pass in null to enable the default interaction
		 * @param interaction
		 */
		public setActiveClickInteraction(interaction : MapClickInteraction) : ng.IPromise<void> {
			return this.mapPromise.then((map) => {
				let interactionFound = false;
				map.getInteractions().forEach((i) => {
					if (i instanceof MapClickInteraction || i instanceof Select) {
						if (i != interaction) {
							i.setActive(false);
						} else {
							interactionFound = true;
							i.setActive(true);
						}
					}
				});

				if (interaction && !interactionFound) {
					map.addInteraction(interaction);
					interaction.setActive(true);
				}
			})
		}

		public getActiveClickInteraction(): ng.IPromise<MapClickInteraction> {
			return this.mapPromise.then(map => {
				return <MapClickInteraction> map.getInteractions().getArray()
					.find(int => {
						return int instanceof MapClickInteraction && int.getActive();
					});
			});
		}

		private dragZoomInteraction: DragZoom = null;
		public setDragZoom(on: boolean = true) {
			return this.mapPromise.then((map) => {
				if (!this.dragZoomInteraction) {
					this.dragZoomInteraction = new DragZoom({
						condition: () => true
					});
					map.addInteraction(this.dragZoomInteraction);
				}
				this.dragZoomInteraction.setActive(on);
			});
		}

		public setDefaultClickInteraction(interaction : MapClickInteraction) {
			if (this.defaultClickInteraction) {
				this.defaultClickInteraction.setActive(false);
			}

			this.defaultClickInteraction = interaction;
			interaction.setActive(true);

			this.mapPromise.then((map) => {
				if (map.getInteractions().getArray().indexOf(interaction) < 0) {
					map.addInteraction(interaction);
				}
			});
		}

		public getDefaultClickInteraction() : MapClickInteraction {
			return this.defaultClickInteraction;
		}

		public setMap(map: olMap) {
			this.map = map;
			this.mapDeferred.resolve(map);
		}
		/**
		 * @param coord
		 * @param zoom - set to null to leave zoom level unaltered
		 * @param animation - "bounce" | "pan"
		 *
		 * Adapted from https://openlayersbook.github.io/ch03-charting-the-map-class/example-04.html
		 */
		public zoomTo(coord: Coordinate, zoom: number = null, animation: ZoomAnimation = null) : void {
			if (!this.map) return;

			switch (animation) {
				case "bounce":
					this.map.getView().animate({
						resolution: this.map.getView().getResolution() * 2
					}, {
						resolution: this.map.getView().getResolution(),
						center: coord
					});
					return;
				case "pan":
					this.map.getView().animate({
						center: coord
					});
					return;
			}

			this.map.getView().setCenter(coord);
			if (zoom !== null && zoom !== undefined) {
				this.map.getView().setZoom(zoom);
			}
		}

		protected mapTarget: HTMLElement | string;

		get interactions(): Interaction[] {
			return this.map.getInteractions().getArray();
		}

		protected _tourActive: boolean = false;

        get keyboardPan(): KeyboardPan {
            return this.interactions.find((int: Interaction): int is KeyboardPan => int instanceof KeyboardPan) || new KeyboardPan();
        }

        get keyboardZoom(): KeyboardZoom {
            return this.interactions.find((int: Interaction): int is KeyboardZoom => int instanceof KeyboardZoom) || new KeyboardZoom();
        }

        updateZoomAndPanActive() {
            const active = !this._tourActive && !this._reportOpen;
            this.keyboardPan.setActive(active);
            this.keyboardZoom.setActive(active);
        }

		get tourActive(): boolean { return this._tourActive };

        set tourActive(activeOrNote: boolean) {
            this._tourActive = activeOrNote;
            this.updateZoomAndPanActive();
        }

        _reportOpen: boolean;

        get reportOpen(): boolean { return this._reportOpen; }

        set reportOpen(openOrNot: boolean) {
            this._reportOpen = openOrNot;
            this.updateZoomAndPanActive();
        }

		attachMap(): void {
			if (this.map && this.mapTarget) {
				this.map.setTarget(this.mapTarget);

				this.map.addInteraction(this.keyboardPan);
				this.map.addInteraction(this.keyboardZoom);

				updateMapSizeWhenVisible(this.map);
				this.updateZoomAndPanActive();
			}
		}

		detachMap(): void {
			if (!this.map) return;

			if (this.map.getTarget()) {
				this.map.removeInteraction(this.keyboardPan);
				this.map.removeInteraction(this.keyboardZoom);

				this.mapTarget = this.map.getTarget();
				this.map.setTarget(null);
			}
		}

		addLayer(node: L.tree.Item, silent: boolean = false): ng.IPromise<void> {
			return this.mapPromise.then(map => {
				node.rootItems = this.treeData;
				if (node.layer) {
					map.addLayer(node.layer);
				}

				if (node.children) node.children.forEach(child => this.addLayer(child));

				if  (!silent) {
					this.$rootScope.$broadcast('updateTreeData');
					this.$rootScope.$broadcast('onTreeChange');
				}
			})
		}

		removeLayer(node: L.tree.Item, silent?: boolean): ng.IPromise<void> {
			return this.mapPromise.then(map => {
				filterNodes(node, i => !!i.layer)
					.forEach(item => {
						map.removeLayer(item.layer);
					});

				let parent = node.parent;
				if (parent) {
					parent.children.splice(parent.children.indexOf(node), 1);
					parent.layerSelectorChildren.splice(parent.layerSelectorChildren.indexOf(node), 1);
				}

				if  (!silent) {
					this.$rootScope.$broadcast('updateTreeData');
					this.$rootScope.$broadcast('onTreeChange');
				}
			});
		}

		nextId(): ng.IPromise<number> {
			return this.promise.then(() => {
				return Math.max(...mapNodes(this.treeData, (n: Item) => n.id).filter(id => id !== undefined && id !== null)) + 1;
			});
		}
	}

	export function init(app = 'smap_v2', mapControlService: ng.Injectable<Function>, coordAdjustmentService: ng.Injectable<Function>): void {
		angular.module('ltMapService', [])
			.service('coordinateAdjustmentService', coordAdjustmentService)
			.service('mapControlService', mapControlService)
			.service('soilPointQueryService', SoilQuery.SoilPointQueryService)
			.service('landcareMapService', LandcareMapService)
			.component('tileErrorModal', new TileErrorModalComponent())
			.service('tileErrorMonitoring', TileErrorMonitoringService);
	}
}
